import { Heading, Panel, Skeleton, Text } from '@afterpaytouch/core'
import { defaultGetBillingAgreementsParams } from '@afterpaytouch/portal-api/consumer/billingAgreements'
import { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useCallback } from 'react'
import { ExtendedPanel, Head } from '../src/component'
import { RecurringPaymentsList } from '../src/component/RecurringPaymentsList'
import { getLayout } from '../src/layouts/MainLayout'
import { ConsumerPage } from '../src/model'
import { useGetBillingAgreementsQuery, useHasBillingAgreement } from '../src/state/billingAgreements/hooks'
import { TrackPageViewed } from '../src/integrations/amplitude'
import { getTheme } from '../src/utils/server/convergence'

const I18N_NAMESPACE = ['common', 'recurringPayments', 'orders', 'index', 'payments', 'account', 'cardScan']

const useBillingAgreementsPageEffects = (): {
  hasBillingAgreement: boolean
  isFetching: boolean
  isLoading: boolean
  isError: boolean
  refetchBillingAgreementsPage: () => void
} => {
  const {
    isFetching,
    isLoading,
    isError: isGetBillingAgreementsQueryError,
    refetch: refetchGetBillingAgreementsQuery,
  } = useGetBillingAgreementsQuery(defaultGetBillingAgreementsParams, {
    refetchOnFocus: false,
  })

  const refetchBillingAgreementsPage = useCallback(() => {
    if (isGetBillingAgreementsQueryError) {
      refetchGetBillingAgreementsQuery()
    }
  }, [isGetBillingAgreementsQueryError, refetchGetBillingAgreementsQuery])

  return {
    hasBillingAgreement: useHasBillingAgreement(),
    isFetching,
    isLoading,
    isError: isGetBillingAgreementsQueryError,
    refetchBillingAgreementsPage,
  }
}

const RecurringPaymentsPage: ConsumerPage = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const pageTitle = `${t('recurringPayments:header')} | ${t('common:brandName')}`

  const { isLoading, isError, refetchBillingAgreementsPage } = useBillingAgreementsPageEffects()

  return (
    <>
      <Head title={pageTitle} />
      <Panel>
        <Panel.Head>
          <>
            <div>
              <Heading size={'XL'} testNameSpace='recurring-payments'>{`${t('recurringPayments:header')}`}</Heading>
              <Text testNameSpace='recurring-payments-subHeader'>{`${t('recurringPayments:subheader')}`}</Text>
            </div>
          </>
        </Panel.Head>
        <Panel.Content bordered inset>
          {isError ? (
            <ExtendedPanel.Error onClick={refetchBillingAgreementsPage} />
          ) : isLoading ? (
            <div className='py-6'>
              <Skeleton />
            </div>
          ) : (
            <RecurringPaymentsList />
          )}
        </Panel.Content>
      </Panel>
      <TrackPageViewed />
    </>
  )
}

RecurringPaymentsPage.getLayout = getLayout()

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { theme } = await getTheme(context)
  return {
    props: {
      theme,
      ...(await serverSideTranslations(context.locale as string, I18N_NAMESPACE)),
    },
  }
}

export default RecurringPaymentsPage
